import React, { useState, useEffect, useContext } from "react";
import {
    makeStyles,
    Paper,
    Grid,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    IconButton,
    Chip,
    Typography,
    Button,
    Box,
    useTheme
} from "@material-ui/core";
import ConfirmationModal from "../ConfirmationModal";
import { Edit as EditIcon, Add as AddIcon, Assignment } from "@material-ui/icons";
import { toast } from "react-toastify";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { AuthContext } from "../../context/Auth/AuthContext";
import PlanModal from "./PlanModal";

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        padding: theme.spacing(1, 0),
    },
    topBar: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "14px 20px",
        marginBottom: theme.spacing(2),
        borderRadius: 12,
        backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
        border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
        boxShadow: theme.palette.type === "dark" ? "0 4px 20px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.05)",
    },
    topBarLeft: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
    },
    topBarTitle: {
        fontSize: "0.95rem",
        fontWeight: 700,
        letterSpacing: "0.5px",
        color: theme.palette.primary.main,
        textTransform: "uppercase",
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    topBarIcon: {
        color: theme.palette.primary.main,
        fontSize: "1.3rem",
    },
    countChip: {
        backgroundColor: `${theme.palette.primary.main}14`,
        color: theme.palette.primary.main,
        fontWeight: 600,
        borderRadius: 13,
        height: 26,
        border: `1px solid ${theme.palette.primary.main}4D`,
        fontSize: "0.75rem",
        padding: "0 8px",
    },
    newButton: {
        borderRadius: 8,
        fontWeight: 700,
        fontSize: "0.88rem",
        textTransform: "none",
        padding: "8px 24px",
        backgroundColor: `${theme.palette.primary.main} !important`,
        color: `${theme.palette.primary.contrastText || "#ffffff"} !important`,
        boxShadow: `0 0 14px ${theme.palette.primary.main}66`,
        transition: "all 0.25s ease",
        "&:hover": {
            backgroundColor: `${theme.palette.primary.main}CC !important`,
            boxShadow: `0 0 18px ${theme.palette.primary.main}99`,
        },
    },
    tableContainer: {
        width: '100%',
        borderRadius: 12,
        overflow: "hidden",
        backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
        border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
        boxShadow: theme.palette.type === "dark" ? "0 4px 20px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.05)",
        ...theme.scrollbarStyles,
    },
    table: {
        width: '100%',
    },
    tableHeader: {
        fontWeight: 700,
        fontSize: "0.78rem",
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        backgroundColor: theme.palette.type === "dark" ? "#1b1e24 !important" : "#F1F5F9 !important",
        color: `${theme.palette.primary.main} !important`,
        borderBottom: `2px solid ${theme.palette.primary.main}4D`,
        padding: "12px 14px",
    },
    tableRow: {
        transition: "background-color 0.2s ease",
        "&:hover": {
            backgroundColor: theme.palette.type === "dark" ? "rgba(0, 229, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
        },
    },
    tableRowInactive: {
        backgroundColor: theme.palette.type === "dark" ? "rgba(239, 68, 68, 0.06)" : "rgba(239, 68, 68, 0.04)",
        transition: "background-color 0.2s ease",
        "&:hover": {
            backgroundColor: theme.palette.type === "dark" ? "rgba(239, 68, 68, 0.12)" : "rgba(239, 68, 68, 0.08)",
        },
    },
    tableCell: {
        borderBottom: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
        padding: "12px 14px",
        color: theme.palette.type === "dark" ? "#FFFFFF" : "#1E293B",
    },
    editButton: {
        color: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}14`,
        border: `1px solid ${theme.palette.primary.main}4D`,
        borderRadius: 8,
        padding: 6,
        transition: "all 0.2s ease",
        "&:hover": {
            backgroundColor: `${theme.palette.primary.main}26`,
            boxShadow: `0 0 10px ${theme.palette.primary.main}66`,
        },
    },
    priceText: {
        color: theme.palette.primary.main,
        fontWeight: 800,
        fontSize: "0.95rem",
    },
}));

export function PlansManagerGrid({ records, onSelect, currency }) {
    const classes = useStyles();
    const theme = useTheme();
    const isLight = theme.palette.type === "light";

    return (
        <Paper className={classes.tableContainer} elevation={0}>
            <Table className={classes.table} padding="none" aria-label="plans table">
                <TableHead>
                    <TableRow>
                        <TableCell align="center" className={classes.tableHeader} style={{ width: '60px' }}>#</TableCell>
                        <TableCell align="left" className={classes.tableHeader} style={{ minWidth: 160 }}>{i18n.t('plans.form.tablePlan')}</TableCell>
                        <TableCell align="left" className={classes.tableHeader} style={{ minWidth: 220 }}>{i18n.t('plans.form.tableLimits')}</TableCell>
                        <TableCell align="center" className={classes.tableHeader} style={{ minWidth: 200 }}>{i18n.t('plans.form.tableEnabledChannels')}</TableCell>
                        <TableCell align="center" className={classes.tableHeader} style={{ minWidth: 160 }}>{i18n.t('plans.form.tableExtraModules')}</TableCell>
                        <TableCell align="center" className={classes.tableHeader} style={{ minWidth: 110 }}>{i18n.t("plans.form.amount")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {records
                        .sort((a, b) => {
                            const getPriority = (name) => {
                                const upperName = (name || "").toUpperCase();
                                if (upperName.includes('DEMO')) return 1;
                                if (upperName.startsWith('BA')) return 2;
                                if (upperName.startsWith('AD')) return 3;
                                if (upperName.startsWith('PR')) return 4;
                                if (upperName.includes('PLANO 1')) return 6;
                                return 5;
                            };
                            const priorityA = getPriority(a.name);
                            const priorityB = getPriority(b.name);
                            if (priorityA !== priorityB) {
                                return priorityA - priorityB;
                            }
                            return (a.name || "").localeCompare(b.name || "");
                        })
                        .map((row) => (
                        <TableRow key={row.id} className={row.isPublic === false ? classes.tableRowInactive : classes.tableRow}>
                            <TableCell align="center" className={classes.tableCell}>
                                <IconButton
                                    className={classes.editButton}
                                    onClick={() => onSelect(row)}
                                    aria-label="edit"
                                    size="small"
                                >
                                    <EditIcon style={{ fontSize: "1.1rem" }} />
                                </IconButton>
                            </TableCell>
                            <TableCell align="left" className={classes.tableCell}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700, color: isLight ? '#0F172A' : '#fff', fontSize: '0.92rem' }}>
                                    {row.name || '-'}
                                </Typography>
                                <Typography variant="caption" style={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.6)', marginTop: 2, display: 'block' }}>
                                    {i18n.t('plans.form.lblTrial')}: <strong style={{ color: row.trial ? theme.palette.primary.main : (isLight ? '#64748B' : '#aaa') }}>{row.trial && row.trialDays ? `${row.trialDays} d` : i18n.t('plans.form.lblNo')}</strong> | {i18n.t('plans.form.lblStatus')}: <strong style={{ color: row.isPublic !== false ? theme.palette.primary.main : (isLight ? '#EF4444' : '#F87171') }}>{row.isPublic !== false ? i18n.t('plans.form.lblActive') : i18n.t('plans.form.lblInactive')}</strong>
                                </Typography>
                            </TableCell>
                            <TableCell align="left" className={classes.tableCell}>
                                <Typography variant="caption" style={{ display: 'block', whiteSpace: 'nowrap', color: isLight ? '#334155' : 'rgba(255, 255, 255, 0.85)' }}>
                                    {i18n.t('plans.form.lblUsers')}: <strong style={{ color: theme.palette.primary.main }}>{row.users || 0}</strong> | {i18n.t('plans.form.lblQueues')}: <strong style={{ color: theme.palette.primary.main }}>{row.queues || 0}</strong>
                                </Typography>
                                <Typography variant="caption" style={{ display: 'block', whiteSpace: 'nowrap', color: isLight ? '#334155' : 'rgba(255, 255, 255, 0.85)' }}>
                                    {i18n.t('plans.form.lblContacts')}: <strong style={{ color: theme.palette.primary.main }}>{row.maxContacts || i18n.t('plans.form.lblUnlimited')}</strong> | {i18n.t('plans.form.lblFlows')}: <strong style={{ color: theme.palette.primary.main }}>{row.maxFlows || i18n.t('plans.form.lblUnlimited')}</strong>
                                </Typography>
                                <Typography variant="caption" style={{ display: 'block', whiteSpace: 'nowrap', color: isLight ? '#334155' : 'rgba(255, 255, 255, 0.85)' }}>
                                    {i18n.t('plans.form.lblBrands')}: <strong style={{ color: theme.palette.primary.main }}>{row.maxBrands || 0}</strong> | {i18n.t('plans.form.lblFunnels')}: <strong style={{ color: theme.palette.primary.main }}>{row.maxFunnels || 0}</strong>
                                </Typography>
                                <Typography variant="caption" style={{ display: 'block', whiteSpace: 'nowrap', color: isLight ? '#334155' : 'rgba(255, 255, 255, 0.85)' }}>
                                    {i18n.t('plans.form.lblMessages')}: <strong style={{ color: theme.palette.primary.main }}>{row.messagesLimit || i18n.t('plans.form.lblUnlimited')}</strong> | {i18n.t('plans.form.lblStatusPosts')}: <strong style={{ color: theme.palette.primary.main }}>{row.maxStatusPosts === -1 ? i18n.t('plans.form.lblUnlimited') : (row.maxStatusPosts || 0)}</strong>
                                </Typography>
                                <Typography variant="caption" style={{ display: 'block', whiteSpace: 'nowrap', color: isLight ? '#334155' : 'rgba(255, 255, 255, 0.85)' }}>
                                    {i18n.t('plans.form.lblGroupPosts')}: <strong style={{ color: theme.palette.primary.main }}>{row.maxGroupPosts === -1 ? i18n.t('plans.form.lblUnlimited') : (row.maxGroupPosts || 0)}</strong> | {i18n.t('plans.form.lblWebWidgets')}: <strong style={{ color: theme.palette.primary.main }}>{row.maxWebWidgets === -1 ? i18n.t('plans.form.lblUnlimited') : (row.maxWebWidgets || 0)}</strong>
                                </Typography>
                            </TableCell>
                            <TableCell align="center" className={classes.tableCell}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', justifyContent: 'center' }}>
                                    <Chip size="small" label={`${i18n.t('plans.form.lblWaQr')}: ${row.maxConnQr || 0}`} style={row.maxConnQr > 0 ? { backgroundColor: '#25D366', color: 'white', fontWeight: 600 } : { opacity: 0.4 }} variant={row.maxConnQr > 0 ? "default" : "outlined"} />
                                    <Chip size="small" label={`${i18n.t('plans.form.lblWaOfficial')}: ${row.maxConnApi || 0}`} style={row.maxConnApi > 0 ? { backgroundColor: '#128C7E', color: 'white', fontWeight: 600 } : { opacity: 0.4 }} variant={row.maxConnApi > 0 ? "default" : "outlined"} />
                                    <Chip size="small" label={`${i18n.t('plans.form.lblFb')}: ${row.maxConnMessenger || 0}`} style={row.maxConnMessenger > 0 ? { backgroundColor: '#1877F2', color: 'white', fontWeight: 600 } : { opacity: 0.4 }} variant={row.maxConnMessenger > 0 ? "default" : "outlined"} />
                                    <Chip size="small" label={`${i18n.t('plans.form.lblIg')}: ${row.maxConnIg || 0}`} style={row.maxConnIg > 0 ? { backgroundColor: '#E1306C', color: 'white', fontWeight: 600 } : { opacity: 0.4 }} variant={row.maxConnIg > 0 ? "default" : "outlined"} />
                                    <Chip size="small" label={`${i18n.t('plans.form.lblTg')}: ${row.maxConnTelegram || 0}`} style={row.maxConnTelegram > 0 ? { backgroundColor: '#0088cc', color: 'white', fontWeight: 600 } : { opacity: 0.4 }} variant={row.maxConnTelegram > 0 ? "default" : "outlined"} />
                                    <Chip size="small" label={`${i18n.t('plans.form.lblEmail')}: ${row.maxConnEmail || 0}`} style={row.maxConnEmail > 0 ? { backgroundColor: '#795548', color: 'white', fontWeight: 600 } : { opacity: 0.4 }} variant={row.maxConnEmail > 0 ? "default" : "outlined"} />
                                </div>
                            </TableCell>
                            <TableCell align="center" className={classes.tableCell}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', justifyContent: 'center' }}>
                                    <Chip
                                        size="small"
                                        label={i18n.t("plans.form.lblMassive")}
                                        style={row.useCampaigns ? { backgroundColor: `${theme.palette.primary.main}1F`, color: theme.palette.primary.main, border: `1px solid ${theme.palette.primary.main}`, fontWeight: 600 } : { opacity: 0.35 }}
                                        variant="outlined"
                                    />
                                    <Chip
                                        size="small"
                                        label={i18n.t("plans.form.lblWavoip")}
                                        style={row.wavoip ? { backgroundColor: `${theme.palette.primary.main}1F`, color: theme.palette.primary.main, border: `1px solid ${theme.palette.primary.main}`, fontWeight: 600 } : { opacity: 0.35 }}
                                        variant="outlined"
                                    />
                                </div>
                            </TableCell>
                            <TableCell align="center" className={classes.tableCell}>
                                <Typography className={classes.priceText}>
                                    {currency} {row.amount ? Number(row.amount).toFixed(2) : '0.00'}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Paper>
    );
}

export default function PlansManager() {
    const classes = useStyles();
    const { list, save, update, remove } = usePlans();

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [planModalOpen, setPlanModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [records, setRecords] = useState([]);
    const [currency, setCurrency] = useState('USD $');
    const [recordToDelete, setRecordToDelete] = useState(null);
    const { user } = useContext(AuthContext);
    const { getAll: getAllSettings } = useCompanySettings();

    const initialDefaultRecord = {
        name: '',
        users: 0,
        connections: 0,
        maxBrands: 1,
        maxFunnels: 1,
        maxConnQr: 1,
        maxConnApi: 1,
        maxConnIg: 1,
        maxConnMessenger: 1,
        maxConnTelegram: 1,
        maxConnEmail: 1,
        queues: 0,
        maxContacts: 0,
        maxFlows: 0,
        messagesLimit: 0,
        maxStatusPosts: 0,
        maxGroupPosts: 0,
        maxCommunityPosts: 0,
        maxWebWidgets: 0,
        amount: 0,
        useWhatsapp: true,
        useFacebook: true,
        useInstagram: true,
        useCampaigns: true,
        useSchedules: true,
        useInternalChat: true,
        useExternalApi: true,
        useKanban: true,
        useOpenAi: true,
        useIntegrations: true,
        isPublic: true,
        useWhatsappOfficial: false,
        trial: false,
        trialDays: 3,
        recurrence: 'monthly',
        wavoip: false,
        paymentLink: ''
    };

    const [record, setRecord] = useState(initialDefaultRecord);

    useEffect(() => {
        async function fetchData() {
            await loadPlans();
            if (user && user.companyId) {
                try {
                    const settings = await getAllSettings(user.companyId);
                    if (settings.system_currency) {
                        setCurrency(settings.system_currency);
                    }
                } catch (err) {
                    console.error("Error loading currency settings", err);
                }
            }
        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const loadPlans = async () => {
        setLoading(true);
        try {
            const planList = await list();
            setRecords(planList);
        } catch (e) {
            toast.error(i18n.t("common.toasts.listError", "Error al listar los planes"));
        }
        setLoading(false);
    };

    const handleNewPlan = () => {
        setRecord(initialDefaultRecord);
        setPlanModalOpen(true);
    };

    const handleSelect = (data) => {
        let useWhatsapp = data.useWhatsapp === false ? false : true;
        let useWhatsappOfficial = data.useWhatsappOfficial === false ? false : true;
        let useFacebook = data.useFacebook === false ? false : true;
        let useInstagram = data.useInstagram === false ? false : true;
        let useCampaigns = data.useCampaigns === false ? false : true;
        let useSchedules = data.useSchedules === false ? false : true;
        let useInternalChat = data.useInternalChat === false ? false : true;
        let useExternalApi = data.useExternalApi === false ? false : true;
        let useKanban = data.useKanban === false ? false : true;
        let useOpenAi = data.useOpenAi === false ? false : true;
        let useIntegrations = data.useIntegrations === false ? false : true;
        let wavoip = data.wavoip === false ? false : true;

        setRecord({
            id: data.id,
            name: data.name || '',
            users: data.users || 0,
            connections: data.connections || 0,
            maxBrands: data.maxBrands || 0,
            maxFunnels: data.maxFunnels || 0,
            maxConnQr: data.maxConnQr || 0,
            maxConnApi: data.maxConnApi || 0,
            maxConnIg: data.maxConnIg || 0,
            maxConnMessenger: data.maxConnMessenger || 0,
            maxConnTelegram: data.maxConnTelegram || 0,
            maxConnEmail: data.maxConnEmail !== undefined && data.maxConnEmail !== null ? data.maxConnEmail : 1,
            queues: data.queues || 0,
            maxContacts: data.maxContacts || 0,
            maxFlows: data.maxFlows ?? 0,
            messagesLimit: data.messagesLimit ?? 0,
            maxStatusPosts: data.maxStatusPosts ?? 0,
            maxGroupPosts: data.maxGroupPosts ?? 0,
            maxCommunityPosts: data.maxCommunityPosts ?? 0,
            maxWebWidgets: data.maxWebWidgets ?? 0,
            amount: data.amount !== undefined && data.amount !== null ? data.amount : 0,
            useWhatsapp,
            useWhatsappOfficial,
            useFacebook,
            useInstagram,
            useCampaigns,
            useSchedules,
            useInternalChat,
            useExternalApi,
            useKanban,
            useOpenAi,
            useIntegrations,
            isPublic: data.isPublic === false ? false : true,
            trial: data.trial === true ? true : false,
            trialDays: data.trialDays || 0,
            recurrence: data.recurrence || 'monthly',
            wavoip,
            paymentLink: data.paymentLink || ''
        });
        setPlanModalOpen(true);
    };

    const handleSubmit = async (data) => {
        setLoading(true);
        try {
            if (data.id) {
                await update(data);
                toast.success(i18n.t("common.toasts.updateSuccess", "Plan actualizado exitosamente"));
            } else {
                await save(data);
                toast.success(i18n.t("common.toasts.saveSuccess", "Plan creado exitosamente"));
            }
            setPlanModalOpen(false);
            await loadPlans();
        } catch (e) {
            toast.error(i18n.t("common.toasts.saveError", "Error al guardar el plan"));
        }
        setLoading(false);
    };

    const handleOpenDeleteDialog = (rec) => {
        setRecordToDelete(rec);
        setShowConfirmDialog(true);
    };

    const handleDelete = async () => {
        if (!recordToDelete) return;
        setLoading(true);
        try {
            await remove(recordToDelete.id);
            toast.success(i18n.t("common.toasts.deleteSuccess", "Plan eliminado exitosamente"));
            setShowConfirmDialog(false);
            setPlanModalOpen(false);
            await loadPlans();
        } catch (e) {
            toast.error(i18n.t("common.toasts.deleteError", "Error al eliminar el plan"));
        }
        setLoading(false);
    };

    return (
        <div className={classes.root} dir={i18n.language === "ar" ? "rtl" : "ltr"}>
            <div className={classes.topBar}>
                <div className={classes.topBarLeft}>
                    <Assignment className={classes.topBarIcon} />
                    <Typography className={classes.topBarTitle}>
                        {i18n.t("plans.title")}
                    </Typography>
                    <Chip
                        className={classes.countChip}
                        label={i18n.t("plans.registeredPlans", { count: records.length })}
                        size="small"
                    />
                </div>
                <Button
                    className={classes.newButton}
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleNewPlan}
                >
                    {i18n.t("plans.buttons.newPlan")}
                </Button>
            </div>

            <PlansManagerGrid
                records={records}
                onSelect={handleSelect}
                currency={currency}
            />

            <PlanModal
                open={planModalOpen}
                onClose={() => setPlanModalOpen(false)}
                initialValue={record}
                onSubmit={handleSubmit}
                onDelete={handleOpenDeleteDialog}
                loading={loading}
            />

            <ConfirmationModal
                title={i18n.t("common.confirmationModal.deleteTitle", "Confirmar Eliminación")}
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={handleDelete}
            >
                {i18n.t("common.confirmationModal.deleteMsg", "¿Estás seguro de que deseas eliminar este registro? Esta acción no se puede deshacer.")}
            </ConfirmationModal>
        </div>
    );
}
